import { PrintRoleType } from '../types';
import { I18n } from '../wrappers/I18n';
import { Device } from '../utilities/Device';
import { Store } from '../wrappers/Store';
import { printService } from '../print/PrintService';
import { CashRegister } from '../models';
import { Settings } from '../wrappers';
import JobQueue from '../utilities/JobQueue';
class FiscalServiceLegacy {
    queue;
    initialized;
    busy;
    basicInformation;
    constructor() {
        this.queue = new JobQueue(250, 60000);
        this.initialized = false;
        this.busy = false;
        this.basicInformation = undefined;
    }
    async init() {
        if (this.busy)
            return;
        const noncashOnly = !!Store.get('tenant')?.venue?.settings?.noncash_only;
        const device = Device.get();
        if (I18n.c('order.registerMethod') !== 'legacy' || noncashOnly || device?.platform !== 'electron')
            return;
        const setupResult = await this.setup();
        if (!setupResult) {
            this.basicInformation = undefined;
            this.initialized = false;
            return;
        }
        this.busy = true;
        const info = await this.getBasicInformation(true);
        this.basicInformation = info;
        this.initialized = info?.data?.ibfm && info.data.ibfm.slice(0, 7) === CashRegister.current()?.activation_code?.slice(0, 7);
        this.busy = false;
    }
    async setup(printer) {
        if (!printer) {
            printer = printService.getAssignedPrinter(PrintRoleType.receipt);
        }
        if (printer?.printer?.name !== 'fp1')
            return false;
        const { requestPath, responsePath } = printer?.options?.other || {};
        await Settings.electron.invoke('fslSetup', { requestPath, responsePath });
        return true;
    }
    async createInvoice(invoice) {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            let result = null;
            if ((invoice.invoiceType === 'Normal' || invoice.invoiceType === 'Advance') && invoice.transactionType === 'Sale') {
                result = await Settings.electron.invoke('fslPrintFiscalReceipt', invoice);
            }
            else if (invoice.invoiceType === 'Normal' && invoice.transactionType === 'Refund') {
                result = await Settings.electron.invoke('fslPrintReclamatoryReceipt', invoice);
            }
            else if (invoice.invoiceType === 'Copy') {
                result = await Settings.electron.invoke('fslPrintDuplicateFiscalReceipt', invoice.referentDocumentNumber, invoice.transactionType === 'Refund');
            }
            else {
                result = { success: false, error: I18n.t('fiscal.fiscal_printer_not_supported') };
            }
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async printReceiptCopy(refNumber, isRefund = false) {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslPrintDuplicateFiscalReceipt', refNumber, isRefund);
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async printText(text, forceCheck = false) {
        if (!this.initialized && !forceCheck)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslPrintText', text);
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async printCurrentStateReport() {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslPrintBalanceSheet');
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async printDailyReport() {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslPrintDailyReport');
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async printPeriodicReport(period) {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslPrintPeriodicReport', period);
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async getBasicInformation(forceCheck = false) {
        if (!this.initialized && !forceCheck)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslGetBasicInformation');
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async withdrawMoney(amount) {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslWithdrawMoney', amount.toFixed(2));
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    async depositMoney(amount) {
        if (!this.initialized)
            return this.formatError({ code: 'not_supported', message: I18n.t('cash_register.error.not_supported') });
        return this.queue.addJob(async () => {
            const result = await Settings.electron.invoke('fslDepositMoney', amount.toFixed(2));
            if (result.error) {
                return this.formatError(result.error);
            }
            return { data: result.data, success: result.success };
        });
    }
    formatError(error) {
        console.log('Response error: ' + (error.message || error.code));
        return { success: false, error: error };
    }
}
const fiscalServiceLegacy = new FiscalServiceLegacy();
export { fiscalServiceLegacy };
export default fiscalServiceLegacy;
//# sourceMappingURL=FiscalServiceLegacy.js.map